// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { getAssets, serializeAssetFilter } from '@/asset/api/get-assets';
import { DetailedAsset } from '@/asset/api/types';
import { FormTextField } from '@/components/form-text-field';
import { SearchResultCountFooter } from '@/components/search-result-count-footer';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { useSupportError } from '@/hooks/use-support-error';
import { QuarantinedSerial, Shipment } from '@/shipment/api/types';
import { useResolveQuarantinedSerial } from '@/shipment/hooks/use-resolve-quarantined-serial';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  PaperProps,
  TextField,
  Typography,
  debounce,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const CANDIDATE_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

const buildFormSchema = (isInbound: boolean) =>
  z.object({
    correctedSerial: z
      .string()
      .min(
        1,
        isInbound
          ? 'Choose the serial that actually arrived'
          : 'Choose the serial that actually shipped'
      ),
    note: z.string().min(1, 'A note is required to resolve a held serial'),
  });

// Built once: an inline factory call would re-allocate the schema per render.
const inboundFormSchema = buildFormSchema(true);
const outboundFormSchema = buildFormSchema(false);

type FormData = z.infer<ReturnType<typeof buildFormSchema>>;

interface Props {
  isOpen: boolean;
  shipmentKey: string;
  quarantinedSerial: QuarantinedSerial;
  isInbound: boolean;
  originLocation: Shipment['fromLocation'];
  // Serials already on the shipment. Excluded from the picker: a serial the
  // shipment already claims cannot also be the corrected unit.
  shipmentSerials: string[];
  onClose: () => void;
}

/**
 * Where Hardfin tracks an asset: its current location, or its last known one
 * while it is in transit.
 */
function assetLocation(asset: DetailedAsset) {
  return asset.currentLocation ?? asset.previousLocation;
}

/**
 * A stand-in row that keeps the shipment origin at the top of the picker even
 * when the asset response has nothing there.
 */
interface OriginPlaceholder {
  placeholder: true;
  locationName: string;
}

type CandidateOption = DetailedAsset | OriginPlaceholder;

function isPlaceholder(option: CandidateOption): option is OriginPlaceholder {
  return 'placeholder' in option;
}

/**
 * The reasons picking this asset looks wrong, each overridable. An asset away
 * from the shipment origin, already in transit, or assigned to a project is a
 * weaker match for the unit that shipped.
 */
function candidateWarnings(
  asset: DetailedAsset,
  originLocation: Props['originLocation']
): string[] {
  const warnings: string[] = [];
  const location = assetLocation(asset);
  if (originLocation !== null && location?.id !== originLocation.id) {
    warnings.push(
      `This asset is at ${location?.name ?? 'an unknown location'}, not the shipment origin (${originLocation.name}). You can still continue, but may want to confirm you have selected the correct serial.`
    );
  }
  if (asset.isInTransit) {
    warnings.push('This asset is already in transit on another shipment');
  }
  if (asset.project !== null) {
    warnings.push('This asset is currently assigned to a project');
  }
  return warnings;
}

/**
 * Resolution dialog: correct the serial. The user asserts the reported serial is
 * the data error and picks the asset of the reported item that actually shipped,
 * from a type-ahead grouped by location. Picking an off-origin, in-transit, or
 * assigned asset raises overridable warnings.
 */
export function QuarantineCorrectSerialDialog(props: Props) {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { showSupportError, hideSupportError } = useSupportError();
  const resolveMutation = useResolveQuarantinedSerial(props.shipmentKey);
  const reportedItem = props.quarantinedSerial.reportedItem;

  // Held onto rather than looked up in candidates: the list is refetched on every
  // search, so a pick would visually clear itself while the form still carried it.
  const [selected, setSelected] = useState<DetailedAsset | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((query: string) => setSearchQuery(query), SEARCH_DEBOUNCE_MS),
    []
  );
  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.clear();
    };
  }, [debouncedSetSearchQuery]);

  // Two pages of candidates: the origin's stock of the reported item, then the
  // item's assets anywhere. The unit came from the shipment origin, so that
  // page leads; the second page covers stale location data.
  const originFilter = {
    pageSize: CANDIDATE_PAGE_SIZE,
    productIds: [reportedItem.id],
    locationIds: props.originLocation === null ? [] : [props.originLocation.id],
    query: searchQuery,
    sort: { property: 'serial' as const, direction: 'asc' as const },
  };
  const originQuery = useQuery({
    queryKey: [
      'asset',
      'quarantine-origin',
      serializeAssetFilter(originFilter),
    ],
    queryFn: () => getAssets(originFilter),
    enabled: props.isOpen && props.originLocation !== null,
  });

  const itemFilter = {
    pageSize: CANDIDATE_PAGE_SIZE,
    productIds: [reportedItem.id],
    query: searchQuery,
    sort: { property: 'serial' as const, direction: 'asc' as const },
  };
  const itemQuery = useQuery({
    queryKey: ['asset', 'quarantine-item', serializeAssetFilter(itemFilter)],
    queryFn: () => getAssets(itemFilter),
    enabled: props.isOpen,
  });

  // A keystroke still inside the debounce window has not reached the server, so
  // the list on screen answers the previous input, not what was typed.
  const isSearching =
    inputValue !== searchQuery ||
    originQuery.isFetching ||
    itemQuery.isFetching;
  // Serials are org-unique CITEXT, so exclude case-insensitively by serial: an
  // asset already on any of the shipment's lines cannot be the corrected unit.
  const shipmentSerialSet = useMemo(
    () => new Set(props.shipmentSerials.map((serial) => serial.toLowerCase())),
    [props.shipmentSerials]
  );
  // The two pages overlap, so merge by asset ID. Origin rows are inserted first
  // and win the slot. fetchedCount counts the merge before the shipment-serial
  // exclusion, so the footer compares like with like against the item's total.
  const { candidates, fetchedCount } = useMemo(() => {
    const merged = new Map<string, DetailedAsset>();
    for (const asset of originQuery.data?.assets ?? []) {
      merged.set(asset.id, asset);
    }
    for (const asset of itemQuery.data?.assets ?? []) {
      if (!merged.has(asset.id)) {
        merged.set(asset.id, asset);
      }
    }
    return {
      candidates: [...merged.values()].filter(
        (asset) => !shipmentSerialSet.has(asset.serial.toLowerCase())
      ),
      fetchedCount: merged.size,
    };
  }, [originQuery.data, itemQuery.data, shipmentSerialSet]);
  const originId = props.originLocation?.id ?? null;
  const originName = props.originLocation?.name ?? null;
  // Origin-first, then grouped by location. Keep the origin at the top even when
  // the response has no assets there, so the user sees it holds none.
  const orderedCandidates = useMemo<CandidateOption[]>(() => {
    // The pick stays an option even when a later search no longer returns it, so
    // the value the form holds is always one the Autocomplete can render.
    const withSelection =
      selected !== null && !candidates.some((asset) => asset.id === selected.id)
        ? [selected, ...candidates]
        : candidates;
    const sorted = [...withSelection].sort((a, b) => {
      const aAtOrigin = originId !== null && assetLocation(a)?.id === originId;
      const bAtOrigin = originId !== null && assetLocation(b)?.id === originId;
      if (aAtOrigin !== bAtOrigin) {
        return aAtOrigin ? -1 : 1;
      }
      const aLocation = assetLocation(a)?.name ?? 'Unknown location';
      const bLocation = assetLocation(b)?.name ?? 'Unknown location';
      return aLocation.localeCompare(bLocation);
    });
    const originHasAssets = sorted.some(
      (asset) => originId !== null && assetLocation(asset)?.id === originId
    );
    if (originName !== null && !originHasAssets) {
      return [{ placeholder: true, locationName: originName }, ...sorted];
    }
    return sorted;
  }, [candidates, originId, originName, selected]);

  const { control, reset, handleSubmit, formState } = useForm<FormData>({
    resolver: zodResolver(
      props.isInbound ? inboundFormSchema : outboundFormSchema
    ),
    defaultValues: { correctedSerial: '', note: '' },
  });

  const warnings =
    selected === null ? [] : candidateWarnings(selected, props.originLocation);

  const totalCandidateCount = itemQuery.data?.totalCount ?? 0;
  const PaperComponent = useMemo(
    () =>
      function PaperWithFooter(paperProps: PaperProps) {
        const { children, ...rest } = paperProps;
        return (
          <Paper {...rest}>
            {children}
            <SearchResultCountFooter
              shownCount={fetchedCount}
              totalCount={totalCandidateCount}
              isSearching={isSearching}
            />
          </Paper>
        );
      },
    [fetchedCount, totalCandidateCount, isSearching]
  );

  const close = (options?: { ignorePending?: boolean }) => {
    if (resolveMutation.isPending && !options?.ignorePending) {
      return;
    }
    props.onClose();
    reset();
    // A keystroke still inside the debounce window would otherwise fire after the
    // dialog closes and re-arm a stale search on a dialog that stays mounted.
    debouncedSetSearchQuery.clear();
    setSelected(null);
    setErrorMessage('');
    setInputValue('');
    setSearchQuery('');
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    setErrorMessage('');
    hideSupportError();
    resolveMutation.mutate(
      {
        quarantineKey: props.quarantinedSerial.key,
        kind: 'CORRECTED_SERIAL',
        note: data.note.trim(),
        correctedSerial: data.correctedSerial,
      },
      {
        onSuccess: () => close({ ignorePending: true }),
        onError: (error) => {
          const apiError = handleAPIError(error);
          if (apiError.message !== '') {
            setErrorMessage(apiError.message);
          } else {
            showSupportError(
              'An error occurred while resolving the held serial.'
            );
          }
        },
      }
    );
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={resolveMutation.isPending ? undefined : () => close()}
      disableEscapeKeyDown={resolveMutation.isPending}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
        sx: { overflow: 'hidden' },
      }}
    >
      <DialogTitle
        sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}
      >
        Correct the serial
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
          The 3PL reported serial{' '}
          <strong>{props.quarantinedSerial.serial}</strong> as{' '}
          <strong>{reportedItem.sku}</strong> ({reportedItem.name}). If the
          serial itself is the data-entry error, choose the {reportedItem.sku}{' '}
          asset that actually {props.isInbound ? 'arrived' : 'shipped'}.
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Corrected serial
        </Typography>
        <Controller
          control={control}
          name="correctedSerial"
          render={({ field, fieldState }) => (
            <Autocomplete<CandidateOption>
              fullWidth
              clearOnBlur={false}
              loading={isSearching}
              loadingText="Searching..."
              options={orderedCandidates}
              inputValue={inputValue}
              onInputChange={(_, value) => {
                setInputValue(value);
                debouncedSetSearchQuery(value);
              }}
              filterOptions={(options) => (isSearching ? [] : options)}
              PaperComponent={PaperComponent}
              value={selected}
              onChange={(_, option) => {
                const asset = option && !isPlaceholder(option) ? option : null;
                setSelected(asset);
                field.onChange(asset?.serial ?? '');
              }}
              getOptionLabel={(option) =>
                isPlaceholder(option) ? 'No matching assets' : option.serial
              }
              getOptionDisabled={(option) => isPlaceholder(option)}
              isOptionEqualToValue={(option, value) =>
                !isPlaceholder(option) &&
                !isPlaceholder(value) &&
                option.serial === value.serial
              }
              groupBy={(option) =>
                isPlaceholder(option)
                  ? option.locationName
                  : (assetLocation(option)?.name ?? 'Unknown location')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS,
                  }}
                  placeholder="Select an asset"
                  size="small"
                  error={fieldState.error !== undefined}
                  helperText={fieldState.error?.message}
                />
              )}
              noOptionsText={
                searchQuery === ''
                  ? `No ${reportedItem.name} serials available`
                  : `No ${reportedItem.name} serials match "${searchQuery}"`
              }
            />
          )}
        />

        {selected !== null && warnings.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {warnings.map((warning) => (
              <Typography key={warning} variant="body2">
                {warning}
              </Typography>
            ))}
          </Alert>
        )}

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
          Note
        </Typography>
        <FormTextField
          control={control}
          name="note"
          fullWidth={true}
          isMultiline={true}
          minimumRows={2}
          placeholder="How was the mixup identified?"
        />
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Button
          onClick={() => close()}
          variant="outlined"
          disabled={resolveMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={formState.isSubmitting || resolveMutation.isPending}
          sx={{ ml: 1.5 }}
        >
          Correct serial
        </Button>
      </DialogActions>
    </Dialog>
  );
}
